import { useQuery } from "@tanstack/react-query";
import { sourceData } from "@/api/sourceData.service";
import type { KeyStats, RollingSubmission, Testimonial } from "@/api/sourceData.service";
import type { SourceDataFirm } from "../../utils/sourceDataFirm";
import { sourceDataOutcome } from "../../utils/sourceDataOutcome";
import type { SourceDataOutcome } from "../../utils/sourceDataOutcome";

type SourceDataInput = {
  firm: SourceDataFirm;
  editionId: string;
  researchBpaId: string;
  /** Requests only while true; a surface passes whether it is on screen. */
  enabled: boolean;
};

/** Key stats for the firm, edition and research BPA, requested only while `enabled` and the firm is known. */
export function useKeyStats(input: SourceDataInput): SourceDataOutcome<KeyStats> {
  const { firm, editionId, researchBpaId, enabled } = input;
  const companyUuid = firm.kind === "known" ? firm.companyUuid : null;
  const query = useQuery({
    queryKey: ["source-data-key-stats", companyUuid, editionId, researchBpaId],
    queryFn: () => sourceData.getKeyStats({ companyUuid: companyUuid!, editionId, researchBpaId }),
    enabled: enabled && companyUuid !== null,
  });
  return sourceDataOutcome(
    firm,
    query,
    response => response.keyStats,
    () => false,
  );
}

/** L500 Live submissions for the firm, edition and research BPA. */
export function useL500Live(input: SourceDataInput): SourceDataOutcome<RollingSubmission[]> {
  const { firm, editionId, researchBpaId, enabled } = input;
  const companyUuid = firm.kind === "known" ? firm.companyUuid : null;
  const query = useQuery({
    queryKey: ["source-data-l500-live", companyUuid, editionId, researchBpaId],
    queryFn: () => sourceData.getL500Live({ companyUuid: companyUuid!, editionId, researchBpaId }),
    enabled: enabled && companyUuid !== null,
  });
  return sourceDataOutcome(
    firm,
    query,
    response => response.rollingSubmissions,
    submissions => submissions.length === 0,
  );
}

/** Testimonials for the firm, edition and research BPA. The filter is applied by the caller, not here. */
export function useTestimonials(input: SourceDataInput): SourceDataOutcome<Testimonial[]> {
  const { firm, editionId, researchBpaId, enabled } = input;
  const companyUuid = firm.kind === "known" ? firm.companyUuid : null;
  const query = useQuery({
    queryKey: ["source-data-testimonials", companyUuid, editionId, researchBpaId],
    queryFn: () =>
      sourceData.getTestimonials({ companyUuid: companyUuid!, editionId, researchBpaId }),
    enabled: enabled && companyUuid !== null,
  });
  return sourceDataOutcome(
    firm,
    query,
    response => response.testimonials,
    testimonials => testimonials.length === 0,
  );
}
